/**
 * Timeout constants for web app requests.
 * - Upload + pipeline: single /train request (upload then backend processing).
 * - Retrain: /retrain request only.
 * - Button requests: delete, metadata, signed URL, cancel, health, download.
 */

/** Time allowed for full upload + pipeline (POST /train). 3 hours. */
export const UPLOAD_AND_PIPELINE_TIMEOUT_MS = 3 * 60 * 60 * 1000;

/** Time allowed for retrain request (POST /retrain). 20 minutes. */
export const RETRAIN_TIMEOUT_MS = 20 * 60 * 1000;

/** Time allowed for button-triggered requests (delete, metadata, signed URL, cancel, health, download). 30 seconds. */
export const BUTTON_REQUEST_TIMEOUT_MS = 30 * 1000;

/** Debounce delay before writing title/description to Firestore (reduces write invocations). 500 ms. */
export const TITLE_DESCRIPTION_DEBOUNCE_MS = 500;

/** Throttle: min interval between backend health-check requests (UserCaptures + CaptureViewer). 8 s. */
export const HEALTH_CHECK_THROTTLE_MS = 8 * 1000;

/** Debounce: delay before calling ensureUserDocument after auth state change. 400 ms. */
export const AUTH_ENSURE_USER_DEBOUNCE_MS = 400;

/** Debounce: delay before fetching capture metadata when captureId/deps change. 300 ms. */
export const CAPTURE_METADATA_DEBOUNCE_MS = 300;

/** Throttle: min interval between Firestore setCaptureStatusFailed writes (same capture). 2 s. */
export const SET_CAPTURE_STATUS_FAILED_THROTTLE_MS = 2 * 1000;

/** Pipeline status polling (usePipelinePolling + local Redis recovery). */
export const PIPELINE_POLL_INTERVAL_MS = 8 * 1000;

/** Consecutive poll failures before showing sustained connection-lost UI. */
export const SUSTAINED_POLL_ERROR_THRESHOLD = 3;

/** After this long stuck at 0–3% with RunPod job, auto-check and re-dispatch (aligns with server watchdog). */
export const RUNPOD_STUCK_REDISPATCH_MS = 2 * 60 * 1000;

/** Mid-job stall when percent/message have not advanced (aligns with server watchdog). */
export const RUNPOD_MID_JOB_STALL_MS = 15 * 60 * 1000;

/** SfM band (roughly 5–74% holistic) — allow longer before client/server redispatch. */
export const RUNPOD_SFM_STALL_MS = 20 * 60 * 1000;

/** After RunPod reports completed, wait briefly for updateCapture before showing failure. */
export const RUNPOD_COMPLETED_CALLBACK_GRACE_MS = 45 * 1000;

/** Queued UI: show long-wait copy and Reconnect GPU after this delay (ms). */
export const RUNPOD_QUEUE_RECONNECT_UI_MS = 90 * 1000;

/** Debounce between manual Reconnect GPU clicks (ms). */
export const RUNPOD_RECONNECT_DEBOUNCE_MS = 30 * 1000;

/** No upload byte progress for this long → treat as interrupted (tab closed / connection lost). */
export const UPLOAD_BYTE_STALL_DETECT_MS = 120 * 1000;

/** Processing capture with no Firestore % for this long → stalled on projects list. */
export const PROCESSING_NO_PERCENT_STALL_MS = 2 * 60 * 1000;
export const PIPELINE_STATUS_FETCH_TIMEOUT_MS = 6 * 1000;
/** 1s tick for progress interpolation (UploadContext synthetic heartbeat + usePipelinePolling display). */
export const PROGRESS_BAR_TICK_MS = 1000;
